/**
 * Process Admin API – processes, subprocesses, templates, tenant assignments.
 * Standalone capability at /api/v1/admin/process-admin (used by benchmarking and tenant profiles).
 */
import api from './api';

const BASE = '/admin/process-admin';

export interface BenchmarkProcess {
  id: string;
  tenant_id: string | null;
  parent_id: string | null;
  name: string;
  area_name: string | null;
  complexity_driver?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProcessSubprocessTableRow {
  area_name: string | null;
  process_id: string;
  process_name: string;
  subprocess_id: string | null;
  subprocess_name: string | null;
}

export const benchmarkProcessesAPI = {
  listProcesses(includeSubprocesses = false): Promise<BenchmarkProcess[]> {
    return api
      .get(`${BASE}/processes`, { params: { includeSubprocesses: includeSubprocesses ? 'true' : undefined } })
      .then((r) => (r.data?.data ?? r.data) || []);
  },

  listSubprocesses(processId: string): Promise<BenchmarkProcess[]> {
    return api
      .get(`${BASE}/processes/${processId}/subprocesses`)
      .then((r) => (r.data?.data ?? r.data) || []);
  },

  getById(id: string): Promise<BenchmarkProcess | null> {
    return api
      .get(`${BASE}/processes/${id}`)
      .then((r) => r.data?.data ?? r.data ?? null);
  },

  listProcessSubprocessTable(): Promise<ProcessSubprocessTableRow[]> {
    return api
      .get(`${BASE}/processes/table`)
      .then((r) => (r.data?.data ?? r.data) || []);
  },

  create(data: { name: string; parent_id?: string | null; area_name?: string | null }): Promise<BenchmarkProcess> {
    return api
      .post(`${BASE}/processes`, data)
      .then((r) => r.data?.data ?? r.data);
  },

  update(id: string, data: { name?: string; area_name?: string | null }): Promise<BenchmarkProcess> {
    return api
      .put(`${BASE}/processes/${id}`, data)
      .then((r) => r.data?.data ?? r.data);
  },

  delete(id: string): Promise<void> {
    return api.delete(`${BASE}/processes/${id}`).then(() => {});
  },

  // Templates
  listTemplates(): Promise<{ id: string; name: string; description: string | null }[]> {
    return api.get(`${BASE}/templates`).then((r) => r.data?.data ?? r.data ?? []);
  },

  createTemplate(data: { name: string; description?: string | null }): Promise<{ id: string; name: string; description: string | null }> {
    return api.post(`${BASE}/templates`, data).then((r) => r.data?.data ?? r.data);
  },

  addTemplateItem(templateId: string, processId: string): Promise<{ id: string; template_id: string; process_id: string }> {
    return api.post(`${BASE}/templates/${templateId}/items`, { process_id: processId }).then((r) => r.data?.data ?? r.data);
  }
};
